/**
 * Riquadro consegna sotto il bottone d'acquisto (come LeiShape): conto alla rovescia fino a mezzanotte
 * di Roma e date di arrivo in giorni lavorativi a partire da oggi a Roma, così le due cose vanno insieme
 * e le date si spostano dopo mezzanotte. Sabato, domenica e feste nazionali italiane non si contano.
 * Se nella stessa sezione c'è l'Offerta LeiDry, valgono i suoi giorni minimi e massimi.
 */

const FUSO = 'Europe/Rome';
const MESI = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];

/** @type {Intl.DateTimeFormat | null} */
let formatoRoma = null;

/**
 * Data e ora di adesso a Roma, qualunque sia il fuso del telefono.
 * @returns {{ anno: number, mese: number, giorno: number, secondi: number }}
 */
function adessoARoma() {
  formatoRoma ??= new Intl.DateTimeFormat('en-GB', {
    timeZone: FUSO,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hourCycle: 'h23',
  });
  /** @type {Record<string, number>} */
  const parti = {};
  for (const parte of formatoRoma.formatToParts(new Date())) {
    if (parte.type !== 'literal') parti[parte.type] = Number(parte.value);
  }
  const ore = (parti.hour ?? 0) % 24;
  return {
    anno: parti.year ?? 1970,
    mese: parti.month ?? 1,
    giorno: parti.day ?? 1,
    secondi: ore * 3600 + (parti.minute ?? 0) * 60 + (parti.second ?? 0),
  };
}

/** @type {Map<number, Set<string>>} */
const festivita = new Map();

/**
 * @param {Date} data - Data a mezzanotte UTC
 * @returns {string} AAAA-MM-GG
 */
function chiave(data) {
  return data.toISOString().slice(0, 10);
}

/**
 * Domenica di Pasqua (calendario gregoriano), a mezzanotte UTC.
 * @param {number} anno
 */
function pasqua(anno) {
  const a = anno % 19;
  const b = Math.floor(anno / 100);
  const c = anno % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const mese = Math.floor((h + l - 7 * m + 114) / 31);
  const giorno = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(Date.UTC(anno, mese - 1, giorno));
}

/**
 * Feste nazionali italiane (i corrieri non consegnano). Stesse regole della data dell'offerta.
 * @param {number} anno
 */
function festeDellAnno(anno) {
  let elenco = festivita.get(anno);
  if (elenco) return elenco;
  const fisse = ['01-01', '01-06', '04-25', '05-01', '06-02', '08-15', '11-01', '12-08', '12-25', '12-26'];
  // San Francesco (4 ottobre) è di nuovo festa nazionale dal 2026.
  if (anno >= 2026) fisse.push('10-04');
  const pasquetta = pasqua(anno);
  pasquetta.setUTCDate(pasquetta.getUTCDate() + 1);
  elenco = new Set([...fisse.map((giorno) => `${anno}-${giorno}`), chiave(pasquetta)]);
  festivita.set(anno, elenco);
  return elenco;
}

/**
 * @param {Date} partenza - Oggi a Roma, a mezzanotte UTC
 * @param {number} giorni - Giorni lavorativi da aggiungere
 */
function piuGiorniLavorativi(partenza, giorni) {
  const data = new Date(partenza.getTime());
  let contati = 0;
  while (contati < giorni) {
    data.setUTCDate(data.getUTCDate() + 1);
    const settimana = data.getUTCDay();
    if (settimana === 0 || settimana === 6) continue;
    if (festeDellAnno(data.getUTCFullYear()).has(chiave(data))) continue;
    contati += 1;
  }
  return data;
}

/** @param {Date} data */
function breve(data) {
  return `${data.getUTCDate()} ${MESI[data.getUTCMonth()]}`;
}

/** @param {number} n */
function due(n) {
  return String(n).padStart(2, '0');
}

class LdyConsegnaElement extends HTMLElement {
  /** @type {number | undefined} */
  #intervallo;
  #giornoMostrato = '';
  #min = 6;
  #max = 10;

  connectedCallback() {
    this.#leggiGiorni();
    this.#aggiorna();
    this.classList.add('is-pronto');
    clearInterval(this.#intervallo);
    this.#intervallo = window.setInterval(() => this.#aggiorna(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.#intervallo);
  }

  /** Giorni dal blocco o, se c'è, dall'Offerta LeiDry della stessa sezione (una sola fonte per i numeri). */
  #leggiGiorni() {
    let min = Number(this.dataset.min) || 6;
    let max = Number(this.dataset.max) || min;

    const contenitore = this.closest('.shopify-section') ?? document;
    const dati = contenitore.querySelector('ldy-offerta-component script[type="application/json"][ref="dati"]');
    if (dati) {
      try {
        const consegna = JSON.parse(dati.textContent || 'null')?.consegna;
        const daMin = Number(consegna?.min);
        const daMax = Number(consegna?.max);
        if (daMin > 0) min = daMin;
        if (daMax > 0) max = daMax;
      } catch {
        // Dati dell'offerta non leggibili: restano i giorni del blocco.
      }
    }

    this.#min = Math.max(1, min);
    this.#max = Math.max(this.#min, max);

    const testoMin = this.querySelector('[data-giorni-min]');
    const testoMax = this.querySelector('[data-giorni-max]');
    if (testoMin && testoMin.textContent !== String(this.#min)) testoMin.textContent = String(this.#min);
    if (testoMax && testoMax.textContent !== String(this.#max)) testoMax.textContent = String(this.#max);
  }

  #aggiorna() {
    const adesso = adessoARoma();
    const mancano = Math.max(0, 86400 - adesso.secondi);

    const timer = this.querySelector('[data-timer]');
    if (timer) {
      const testo = `${due(Math.floor(mancano / 3600))}:${due(Math.floor((mancano % 3600) / 60))}:${due(mancano % 60)}`;
      if (timer.textContent !== testo) timer.textContent = testo;
    }

    const oggi = `${adesso.anno}-${adesso.mese}-${adesso.giorno}`;
    if (oggi === this.#giornoMostrato) return;
    this.#giornoMostrato = oggi;

    const partenza = new Date(Date.UTC(adesso.anno, adesso.mese - 1, adesso.giorno));
    const da = piuGiorniLavorativi(partenza, this.#min);
    const a = piuGiorniLavorativi(partenza, this.#max);
    const date = this.querySelector('[data-date]');
    if (date) date.textContent = this.#min === this.#max ? breve(a) : `${breve(da)} – ${breve(a)}`;
    // Frase «Ordina oggi: arriva tra il [da] e il [a]» (impianto del riquadro «scorte» del modello, con date vere).
    const primo = this.querySelector('[data-da]');
    const ultimo = this.querySelector('[data-a]');
    if (primo) primo.textContent = breve(da);
    if (ultimo) ultimo.textContent = breve(a);
  }
}

if (!customElements.get('ldy-consegna')) {
  customElements.define('ldy-consegna', LdyConsegnaElement);
}
